import React, { useContext } from 'react'

import { TitleContext } from '@providers/TitleProvider'
import { useSession } from '@stores/globalData/globalData.store'

import CoverArt from '../../../shared/CoverArt'
import DetailsSection from '../DetailsSection'
import PremiumAd from '../PremiumAd'
import OwnerControls from './OwnerControls'
import PublisherControls from './PublisherControls'
import PurchaseControls from './PurchaseControls'
import SellerControls from './SellerControls'

const Controls: React.FC = () => {
  const { title } = useContext(TitleContext)
  const session = useSession()

  const isSeller = title.isSeller(session.currentUser?.external_id)

  /**
   * Purchase is required if:
   * - They are not the seller of the title
   * - They do not already own the title (have previously purchased)
   * - They are in the default community and are NOT in the Learn content preview
   * - And one of the following:
   *   - The title is user-generated
   *     -- OR --
   *   - They do not have a premium subscription
   *
   * The owner controls have specific handlers for Learn users without premium features. You cannot
   * purchase titles from Learn so purchase is NOT required for these users (but their access is
   * still limited).
   */
  const purchaseRequired =
    !isSeller &&
    !title.isOwnedByUser() &&
    session.isNonLearnCommunity() &&
    !title.complimentary &&
    (title.isUserGen() || !session.hasPremiumFeatures())

  function ControlsComponent(): JSX.Element {
    // If the user is the "seller" of a community title, show the publisher controls
    if (isSeller && title.isCommunity()) return <PublisherControls />

    // If the user is the "seller" of any other type of title, show the seller controls
    if (isSeller) return <SellerControls />

    // If purchase is required, show the purchase controls
    if (purchaseRequired) return <PurchaseControls />

    // Otherwise, show the owner controls
    return <OwnerControls />
  }

  return (
    <DetailsSection>
      <div className="flex flex-col items-center justify-between gap-4 py-1 pb-5 md:flex-row md:items-start">
        <CoverArt url={title.metadata.cover_art_url} overlay={title.metadata.cover_art_directive} />
        <div className="flex flex-col justify-between flex-1 relative h-[163px]">
          <ControlsComponent />
        </div>
      </div>
      {purchaseRequired && !title.isUserGen() && <PremiumAd />}
    </DetailsSection>
  )
}

export default Controls
